import {action, computed, observable} from 'mobx';
import _ from 'lodash';
import {successAlert, errorAlert} from 'shared/tools';
import {endpoints, types, auth} from 'main/tools';
import {t, locale} from 'shared/core';
import {v4 as uuidv4} from 'uuid';
import Document from 'main/stores/documents/Document';
import CompanyDocument from 'main/stores/documents/CompanyDocument';
import Folder from 'main/stores/folders/Folder';
import CompanyDocumentAssignment from 'main/stores/documents/CompanyDocumentAssignment';
import Employee from 'main/stores/employees/Employee';
import {DomainStore, fetchModels} from 'shared/store';

class EmployeeDocumentState {
  store = new DomainStore();

  @observable editingDocument = {};
  @observable editDocumentModalOpen = false;
  @observable errors = {};
  @observable presigner;
  @observable employee;
  @observable movingDocument = {};
  @observable documentAction = '';
  @observable companyDocumentAssignment;
  @observable moveDocumentModalOpen = false;
  @observable allFolders = [];
  @observable companyDocuments;
  @observable assignDocumentModalOpen = false;
  @observable assigningDocument = {};
  @observable acceptedMimeTypes;
  @observable isUploading = false;
  @observable annotatorModalOpen = false;
  @observable interactiveAgent = null;
  @observable uploadModalFolder = {};
  @observable uploadModalShareWithEmployee = false;
  @observable uploadModalFiles = [];
  @observable uploadModalOpen = false;

  constructor(parentState) {
    this.parentState = parentState;
    this.presigner = endpoints.EMPLOYEE_DOCUMENTS.PRESIGNER.with(this.employeeId);
  }

  @action async load() {
    const composed = await this.store._compose([
      endpoints.FOLDERS.FOR_EMPLOYEE.ALL.with(this.employeeId)
    ]);

    if (auth.hasAccess('::MANAGE_DOCUMENTS')) {
      this.companyDocuments = await fetchModels(
        endpoints.COMPANY_DOCUMENTS.all,
        types.COMPANY_DOCUMENT
      );
    }

    this.employee = new Employee(this.store._getSingle(types.EMPLOYEE, {id: this.employeeId}));
    this.allFolders = composed[0].data.map(f => new Folder(f));
  }

  @action async handleUploadModalSave() {
    this.isUploading = true;
    try {
      const documentUploads = this.uploadModalFiles.map(async (file) => {
        await this.createDocument(file, this.uploadModalFolder, this.uploadModalShareWithEmployee);
      });

      await Promise.all(documentUploads);
    } finally {
      this.isUploading = false;
    }
    this.closeUploadModal();
  }

  @action setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  @action async fetchFolderDocuments(folder) {
    const store = new DomainStore();
    const endpointsToLoad = [endpoints.EMPLOYEE_DOCUMENTS.withFilter(this.employeeId, folder.id)];

    if (folder.hasSubfolders) {
      endpointsToLoad.push(endpoints.FOLDERS.FOR_EMPLOYEE.with(this.employeeId, folder.id));
    }

    await store._compose(endpointsToLoad);
    folder.documents = store._getAll(types.DOCUMENT, Document);

    if (folder.hasSubfolders) {
      folder.subfolders = store._getAll(
        types.FOLDER,
        subfolder => subfolder.parentId?.toString() === folder.id,
        Folder
      );
    }
  }

  @action openUploadModal(folder) {
    this.uploadModalFolder = folder || {};
    this.uploadModalShareWithEmployee = false;
    this.uploadModalFiles = [];
    this.uploadModalOpen = true;
  }

  @action closeUploadModal() {
    this.uploadModalFolder = {};
    this.uploadModalShareWithEmployee = false;
    this.uploadModalFiles = [];
    this.uploadModalOpen = false;
  }

  @action openAssignDocumentModal(folder) {
    this.assigningDocument = new CompanyDocument({
      id: uuidv4(),
      locale: locale,
      folderId: _.get(folder, 'id'),
    });
    this.documentAction = '';
    this.companyDocumentAssignment = new CompanyDocumentAssignment({
      employees: [this.parentState.employee],
    });
    this.assignDocumentModalOpen = true;
  }

  @action openEditDocumentModal(model) {
    this.editingDocument = new Document(model);
    this.editDocumentModalOpen = true;
  }

  @action closeEditDocumentModal() {
    this.editingDocument = {};
    this.editDocumentModalOpen = false;
  }

  @action async createDocument(file, folder, shareWithEmployee = false) {
    const doc = new Document({
      key: file.key,
      fileName: file.fileName,
      fileSize: file.fileSize,
      fileType: 'Employee File',
      contentType: file.contentType || 'application/octet-stream',
      shareWithEmployee: shareWithEmployee,
      folder: folder
    });

    const {model, errors} = await this.store.post(
      endpoints.EMPLOYEE_DOCUMENTS.with(this.employeeId),
      types.DOCUMENT,
      doc
    );

    if (model) {
      this.refreshTable();
    } else {
      errorAlert(
        t('components.upload.FAILED_TO_UPLOAD', { file: file.name, message: errors.contentType })
      );
    }
  }

  @action async updateDocument() {
    const { model, errors } = await this.store.patch(this.editingDocument);
    this.errors = errors;

    if (model) {
      successAlert(t('employees.profile.documents.alerts.Successfully updated document'));
      this.refreshTable();
      this.closeEditDocumentModal();
    }
  }

  @action async deleteDocument(model) {
    await this.store.destroy(model);
    this.refreshTable();
  }

  @action openMoveDocumentModal(model) {
    this.movingDocument = model;
    this.movingDocument.folder = null;
    this.moveDocumentModalOpen = true;
  }

  @action closeMoveDocumentModal() {
    this.moveDocumentModalOpen = false;
  }

  @action async moveDocumentToFolder() {
    const {model} = await this.store.patch(this.movingDocument);

    if (model) {
      successAlert(t('employees.profile.documents.alerts.Successfully moved document'));
      this.refreshTable();
      this.closeMoveDocumentModal();
    }
  }

  @action closeAssignDocumentModal() {
    this.assignDocumentModalOpen = false;
    setTimeout(() => {
      this.assigningDocument = {};
      this.errors = {};
    }, 500);
  }

  @action openAnnotatorModal(document) {
    this.assignDocumentModalOpen = false;
    if (document) {
      this.assignDocument = document;
    }
    this.annotatorModalOpen = true;
  }

  @action closeAnnotatorModal() {
    this.annotatorModalOpen = false;
    setTimeout(() => {
      this.assigningDocument = {};
      this.errors = {};
    }, 500);
  }

  @action selectDocumentType(type) {
    this.assigningDocument.merge({ documentType: type });
    this.acceptedMimeTypes = type === 'pdf_template' ? 'application/pdf' : '';
  }

  @action selectDocumentAction(action) {
    this.documentAction = action;
  }

  @action async saveUploadedFile() {
    if (!this.validateCompanyDocument()) return;
    if (this.uploadingPDFTemplate) {
      this.openAnnotatorModal();
    } else {
      await this.assignDocument();
    }
  }

  @action async assignExistingDocument() {
    const {model, errors} = await this.store.post(
      endpoints.DOCUMENT_ACTIONS.with(this.companyDocumentAssignment.companyDocument.id),
      types.COMPANY_DOCUMENT_ASSIGNMENT,
      this.companyDocumentAssignment
    );
    this.errors = errors;

    if (model) {
      this.closeAssignDocumentModal();
      successAlert(t('employees.profile.documents.alerts.Successfully assigned document'));
    }
  }

  @action async addAnnotations(annotations) {
    this.assigningDocument.annotations = annotations;
    if (!this.validateCompanyDocument()) return;
    await this.assignDocument();
  }

  @action async assignDocument() {
    const {model, errors} = await this.store.put(
      endpoints.ASSIGN_COMPANY_DOCUMENT.with(this.employeeId),
      types.COMPANY_DOCUMENT,
      new CompanyDocument(this.assigningDocument)
    );

    this.errors = errors;

    if (model) {
      this.closeAnnotatorModal();
      this.closeAssignDocumentModal();
      successAlert(t('employees.profile.documents.alerts.Successfully assigned document'));
    }
  }

  refreshTable() {
    if (this.interactiveAgent) {
      this.interactiveAgent.refresh();
      this.interactiveAgent.refreshFolders();
    }
  }

  documentCustomLinks(model) {
    if (_.isEmpty(this.allFolders)) return [];
    if (!model.hasLink('self')) return [];

    return [{
      order: 1,
      text: 'employees.profile.documents.Move to Folder...',
      action: () => this.openMoveDocumentModal(model),
    }];
  }

  @computed get customFolderLinks() {
    const {parentState} = this;
    if (!parentState.editDataPermission('::DOCUMENTS')) return null;

    return [
      {
        order: 1,
        text: 'employees.profile.documents.Upload File(s)',
        action: folder => this.openUploadModal(folder),
      },
      {
        order: 2,
        text: 'employees.profile.documents.Assign Document',
        action: folder => this.openAssignDocumentModal(folder),
      }
    ];
  }

  @computed get choosingDocumentAction() {
    return !this.uploadingNewDocument && !this.assigningExistingDocument;
  }

  @computed get choosingDocumentType() {
    return !this.uploadingPDFTemplate && !this.uploadingStaticDocument;
  }

  @computed get uploadingNewDocument() {
    return this.documentAction === 'new';
  }

  @computed get assigningExistingDocument() {
    return this.documentAction === 'existing';
  }

  @computed get uploadingPDFTemplate() {
    return this.assigningDocument.documentType === 'pdf_template';
  }

  @computed get uploadingStaticDocument() {
    return this.assigningDocument.documentType === 'static_document';
  }

  @computed get employeeId() {
    return this.parentState.match.params.id;
  }

  @computed get canShareWithEmployee() {
    return auth.employee.id !== this.employeeId && this.parentState.editDataPermission('::DOCUMENTS');
  }

  @computed get foldersInModalDropdown() {
    return this.allFolders;
  }

  validateCompanyDocument() {
    this.errors = {};

    if (!this.assigningDocument.name) {
      this.errors.name = t('employees.profile.documents.errors.Name is required.');
    }

    if (_.isEmpty(this.assigningDocument.originalFile)) {
      this.errors.awsFiles = t('employees.profile.documents.errors.A file must be uploaded.');
    }

    return _.isEmpty(this.errors);
  }
}

export default EmployeeDocumentState;
